import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Badge,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  NumberInput,
  NumberInputField,
  Select,
  SimpleGrid,
  Spinner,
  Switch,
  Text,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { FaEdit } from 'react-icons/fa';
import Card from 'components/card/Card';
import SubscriptionPlanService from 'services/subscriptionPlan.api';
import { SubscriptionPlan } from 'services/@types';
import FeaturesTable from './components/FeaturesTable';

interface EditForm {
  name: string;
  description: string;
  period: 'month' | 'year';
  priceIls: number;
  priceUsd: number;
  priceEur: number;
  priceGbp: number;
  grade: number;
  freeTrialDays: number;
  order: number;
  design: string;
  visible: boolean;
  isMostPopular: boolean;
}

function planToForm(plan: SubscriptionPlan): EditForm {
  return {
    name: plan.name || '',
    description: plan.description || '',
    period: plan.period || 'month',
    priceIls: plan.price?.ils ?? 0,
    priceUsd: plan.price?.usd ?? 0,
    priceEur: plan.price?.eur ?? 0,
    priceGbp: plan.price?.gbp ?? 0,
    grade: plan.grade ?? 0,
    freeTrialDays: plan.freeTrialDays ?? 0,
    order: plan.order ?? 0,
    design: plan.design || '',
    visible: plan.visible ?? false,
    isMostPopular: plan.isMostPopular ?? false,
  };
}

export default function SubscriptionPlanDetail() {
  const { id } = useParams<{ id: string }>();
  const toast = useToast();
  const textColor = useColorModeValue('secondaryGray.900', 'white');

  const [plan, setPlan] = useState<SubscriptionPlan | null>(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<EditForm | null>(null);

  const fetchPlan = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const data = await SubscriptionPlanService.getSubscriptionPlan(id);
      setPlan(data);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load subscription plan',
        status: 'error',
        duration: 3000,
      });
    }
    setLoading(false);
  }, [id, toast]);

  useEffect(() => {
    fetchPlan();
  }, [fetchPlan]);

  const handleEdit = () => {
    if (plan) {
      setForm(planToForm(plan));
      setIsEditing(true);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setForm(null);
  };

  const handleSave = async () => {
    if (!id || !form) return;
    setSaving(true);
    try {
      await SubscriptionPlanService.updateSubscriptionPlan(id, {
        name: form.name,
        description: form.description,
        period: form.period,
        price: {
          ils: form.priceIls,
          usd: form.priceUsd,
          eur: form.priceEur,
          gbp: form.priceGbp,
        },
        grade: form.grade,
        freeTrialDays: form.freeTrialDays,
        order: form.order,
        design: form.design,
        visible: form.visible,
        isMostPopular: form.isMostPopular,
      });
      toast({
        title: 'Success',
        description: 'Subscription plan updated',
        status: 'success',
        duration: 3000,
      });
      setIsEditing(false);
      setForm(null);
      await fetchPlan();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update subscription plan',
        status: 'error',
        duration: 3000,
      });
    }
    setSaving(false);
  };

  const updateForm = (field: keyof EditForm, value: any) => {
    setForm((prev) => (prev ? { ...prev, [field]: value } : prev));
  };

  if (loading) {
    return (
      <Flex
        direction="column"
        pt={{ sm: '125px', lg: '75px' }}
        align="center"
        justify="center"
        minH="50vh"
      >
        <Spinner size="xl" color="brand.500" />
      </Flex>
    );
  }

  if (!plan) {
    return (
      <Flex direction="column" pt={{ sm: '125px', lg: '75px' }} align="center">
        <Text color="red.500" fontSize="lg">
          Plan not found
        </Text>
      </Flex>
    );
  }

  return (
    <Flex direction="column" pt={{ sm: '125px', lg: '75px' }} gap="20px">
      <Card p="30px">
        <Flex justify="space-between" align="start" mb="20px">
          {isEditing && form ? (
            <Box flex="1" mr="16px">
              <FormControl mb="12px">
                <FormLabel fontSize="sm" color="gray.500">
                  Name
                </FormLabel>
                <Input
                  value={form.name}
                  onChange={(e) => updateForm('name', e.target.value)}
                  fontSize="md"
                />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm" color="gray.500">
                  Description
                </FormLabel>
                <Input
                  value={form.description}
                  onChange={(e) => updateForm('description', e.target.value)}
                  fontSize="md"
                />
              </FormControl>
            </Box>
          ) : (
            <Box>
              <Text color={textColor} fontSize="2xl" fontWeight="700">
                {plan.name}
              </Text>
              <Text color="gray.500" fontSize="sm">
                {plan.description || 'No description'}
              </Text>
            </Box>
          )}
          <Flex gap="8px" align="center">
            {!isEditing && (
              <>
                <Badge
                  colorScheme={plan.period === 'year' ? 'purple' : 'blue'}
                  fontSize="md"
                  px="12px"
                  py="4px"
                  borderRadius="8px"
                >
                  {plan.period}
                </Badge>
                <Badge
                  colorScheme={plan.visible ? 'green' : 'gray'}
                  fontSize="md"
                  px="12px"
                  py="4px"
                  borderRadius="8px"
                >
                  {plan.visible ? 'Visible' : 'Hidden'}
                </Badge>
              </>
            )}
            {!isEditing && (
              <Button
                leftIcon={<FaEdit />}
                size="sm"
                variant="outline"
                colorScheme="brand"
                onClick={handleEdit}
                ml="8px"
              >
                Edit
              </Button>
            )}
          </Flex>
        </Flex>

        {isEditing && form ? (
          <>
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap="20px" mb="20px">
              <FormControl>
                <FormLabel fontSize="sm" color="gray.500">
                  Period
                </FormLabel>
                <Select
                  value={form.period}
                  onChange={(e) =>
                    updateForm('period', e.target.value as 'month' | 'year')
                  }
                >
                  <option value="month">month</option>
                  <option value="year">year</option>
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm" color="gray.500">
                  Price (ILS)
                </FormLabel>
                <NumberInput
                  value={form.priceIls}
                  onChange={(_, val) => updateForm('priceIls', val || 0)}
                  min={0}
                >
                  <NumberInputField />
                </NumberInput>
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm" color="gray.500">
                  Price (USD)
                </FormLabel>
                <NumberInput
                  value={form.priceUsd}
                  onChange={(_, val) => updateForm('priceUsd', val || 0)}
                  min={0}
                >
                  <NumberInputField />
                </NumberInput>
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm" color="gray.500">
                  Price (EUR)
                </FormLabel>
                <NumberInput
                  value={form.priceEur}
                  onChange={(_, val) => updateForm('priceEur', val || 0)}
                  min={0}
                >
                  <NumberInputField />
                </NumberInput>
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm" color="gray.500">
                  Price (GBP)
                </FormLabel>
                <NumberInput
                  value={form.priceGbp}
                  onChange={(_, val) => updateForm('priceGbp', val || 0)}
                  min={0}
                >
                  <NumberInputField />
                </NumberInput>
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm" color="gray.500">
                  Grade
                </FormLabel>
                <NumberInput
                  value={form.grade}
                  onChange={(_, val) => updateForm('grade', val || 0)}
                  min={0}
                >
                  <NumberInputField />
                </NumberInput>
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm" color="gray.500">
                  Free Trial Days
                </FormLabel>
                <NumberInput
                  value={form.freeTrialDays}
                  onChange={(_, val) => updateForm('freeTrialDays', val || 0)}
                  min={0}
                >
                  <NumberInputField />
                </NumberInput>
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm" color="gray.500">
                  Order
                </FormLabel>
                <NumberInput
                  value={form.order}
                  onChange={(_, val) => updateForm('order', val || 0)}
                  min={0}
                >
                  <NumberInputField />
                </NumberInput>
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm" color="gray.500">
                  Design
                </FormLabel>
                <Input
                  value={form.design}
                  onChange={(e) => updateForm('design', e.target.value)}
                />
              </FormControl>
              <FormControl display="flex" alignItems="center">
                <FormLabel fontSize="sm" color="gray.500" mb="0">
                  Visible
                </FormLabel>
                <Switch
                  isChecked={form.visible}
                  onChange={(e) => updateForm('visible', e.target.checked)}
                  colorScheme="brand"
                />
              </FormControl>
              <FormControl display="flex" alignItems="center">
                <FormLabel fontSize="sm" color="gray.500" mb="0">
                  Most Popular
                </FormLabel>
                <Switch
                  isChecked={form.isMostPopular}
                  onChange={(e) => updateForm('isMostPopular', e.target.checked)}
                  colorScheme="brand"
                />
              </FormControl>
            </SimpleGrid>
            <Flex gap="12px" justify="flex-end">
              <Button
                variant="outline"
                onClick={handleCancel}
                isDisabled={saving}
              >
                Cancel
              </Button>
              <Button
                colorScheme="brand"
                onClick={handleSave}
                isLoading={saving}
              >
                Save
              </Button>
            </Flex>
          </>
        ) : (
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap="20px">
            <Box>
              <Text fontSize="sm" color="gray.500">
                Price (ILS)
              </Text>
              <Text fontSize="md" color={textColor} fontWeight="600">
                {plan.price?.ils != null ? `${plan.price.ils} ILS` : '-'}
              </Text>
            </Box>
            <Box>
              <Text fontSize="sm" color="gray.500">
                Price Per Month (ILS)
              </Text>
              <Text fontSize="md" color={textColor}>
                {plan.pricePerMonth?.ils != null
                  ? `${plan.pricePerMonth.ils} ILS`
                  : '-'}
              </Text>
            </Box>
            <Box>
              <Text fontSize="sm" color="gray.500">
                Price With Discount (ILS)
              </Text>
              <Text fontSize="md" color={textColor}>
                {plan.priceWithDiscount?.ils != null
                  ? `${plan.priceWithDiscount.ils} ILS`
                  : '-'}
              </Text>
            </Box>
            <Box>
              <Text fontSize="sm" color="gray.500">
                Grade
              </Text>
              <Text fontSize="md" color={textColor}>
                {plan.grade ?? '-'}
              </Text>
            </Box>
            <Box>
              <Text fontSize="sm" color="gray.500">
                Free Trial Days
              </Text>
              <Text fontSize="md" color={textColor}>
                {plan.freeTrialDays ?? '-'}
              </Text>
            </Box>
            <Box>
              <Text fontSize="sm" color="gray.500">
                Order
              </Text>
              <Text fontSize="md" color={textColor}>
                {plan.order ?? '-'}
              </Text>
            </Box>
            <Box>
              <Text fontSize="sm" color="gray.500">
                Most Popular
              </Text>
              <Badge
                colorScheme={plan.isMostPopular ? 'yellow' : 'gray'}
                fontSize="sm"
                borderRadius="6px"
              >
                {plan.isMostPopular ? 'Yes' : 'No'}
              </Badge>
            </Box>
            <Box>
              <Text fontSize="sm" color="gray.500">
                Design
              </Text>
              <Text fontSize="md" color={textColor}>
                {plan.design || '-'}
              </Text>
            </Box>
            <Box>
              <Text fontSize="sm" color="gray.500">
                Discount (Month / Year)
              </Text>
              <Text fontSize="md" color={textColor}>
                {plan.discount
                  ? `${plan.discount.month}% / ${plan.discount.year}%`
                  : '-'}
              </Text>
            </Box>
          </SimpleGrid>
        )}
      </Card>

      <FeaturesTable features={plan.subscriptionPlanFeatures || []} />
    </Flex>
  );
}
